/**
 * WebAuthService
 * Handles web-specific authentication flows:
 *  - Email + OTP (verification code) login  (3-step account service flow)
 *  - WebAuthn / Passkey login
 *  - Session persistence in localStorage
 */

import type { ApiService } from '@pro-groups/shared';
import { AUTH_STORAGE_KEYS, ALL_AUTH_STORAGE_KEY_VALUES } from '@pro-groups/shared';

// Backend constants – must match API_CONSTANTS in shared/config/constants.ts
const ACCT_SIG    = 'yBwo314C-O9lyrDq91RrbwcAQl23wpdBHcU0xiNCgCg';
const DEFAULT_ACCESS_TYPE = '10';
// "Patient Tools" — default SkinType for any sign-in flow that doesn't request a
// specific branded skin via AuthFlowOptions.skinType (see auth/authEntry.ts's
// resolveSkinTypeFromPath, which is what callers derive this from).
const DEFAULT_SKIN_TYPE = '3';
const CLIENT_ID   = '7';

/**
 * Keys used in localStorage. The keys shared with `userStore`'s session state
 * (token/refresh/email/timestamp/session/settings/access-type/nav-reset) are
 * sourced from the shared `AUTH_STORAGE_KEYS` so this file can never drift out
 * of sync with what `userStore.login`/`applyAuthSession` write or `logout` clears.
 *
 * NOTE: `persistSession()` writes directly to `window.localStorage` instead of
 * going through `userStore.applyAuthSession()` because it runs during the
 * sign-in flow, before `ChildNav`'s `useMemo` has called `createStoresWithStorage()` —
 * the Zustand userStore and its global registry (`__proGroupsStores`/
 * `__childNavStores`) do not exist yet at this point, so there is nothing to
 * delegate to. `userStore.checkStoredAuth()` picks up the `userSession` key
 * written here once the store does mount.
 */
const AUTH_KEYS = {
  TOKEN:      AUTH_STORAGE_KEYS.TOKEN,
  REFRESH:    AUTH_STORAGE_KEYS.REFRESH_TOKEN,
  EMAIL:      AUTH_STORAGE_KEYS.USER_EMAIL,
  PASSWORD:   'user_password',
  SIGNATURE:  'user_signature',
  USER_NAME:  'user_name',
  USER_SETTINGS: AUTH_STORAGE_KEYS.USER_SETTINGS,
  SESSION:    AUTH_STORAGE_KEYS.USER_SESSION,
  TIMESTAMP:  AUTH_STORAGE_KEYS.TIMESTAMP,
  PASSKEY_ID: 'passkey_credential_id',
} as const;

const SETTING_NAME_TO_STORAGE_KEY: Record<string, string> = {
  track_prescriptions:           'wellcheck_track_prescriptions',
  track_referrals:               'wellcheck_track_referrals',
  track_vaccinations:            'wellcheck_vaccine_info_enabled',
  track_height_weight:           'wellcheck_track_height_weight',
  measurement_units:             'wellcheck_measurement_units',
  language:                      'language',
  milestone_staleness_threshold: 'milestone_staleness_threshold',
  concern_freshness_threshold:   'concern_freshness_threshold',
  action_freshness_threshold:    'action_freshness_threshold',
  auto_signout_timeout:          'auto_signout_timeout',
  wellcheck_schedule_id:         'wellcheck_selected_schedule_id',
  vaccine_schedule_id:           'wellcheck_selected_vaccine_schedule_id',
};

const PREF_STORAGE_DEFAULTS: Record<string, string> = {
  wellcheck_track_prescriptions:  'true',
  wellcheck_track_referrals:      'true',
  wellcheck_vaccine_info_enabled: 'true',
  wellcheck_track_height_weight:  'true',
  wellcheck_measurement_units:    'english',
  language:                       'en',
  milestone_staleness_threshold:  '1month',
  concern_freshness_threshold:    '1month',
  action_freshness_threshold:     '1month',
  auto_signout_timeout:           'disabled',
};

function applySettingsToLocalStorage(settings: Array<{ Name: string; Value: string }> | undefined): void {
  try {
    for (const [key, defaultValue] of Object.entries(PREF_STORAGE_DEFAULTS)) {
      window.localStorage.setItem(key, defaultValue);
    }
    window.localStorage.removeItem('wellcheck_selected_schedule_id');
    window.localStorage.removeItem('wellcheck_selected_vaccine_schedule_id');

    for (const setting of settings ?? []) {
      const storageKey = SETTING_NAME_TO_STORAGE_KEY[setting.Name];
      if (storageKey && setting.Value != null) {
        window.localStorage.setItem(storageKey, String(setting.Value));
      }
    }
    window.localStorage.removeItem('preferences_dirty_flag');
  } catch {
    // Ignore storage errors; auth success should not be blocked by local preference writes.
  }
}

export interface StoredSession {
  isAuthenticated: boolean;
  email: string;
  token: string;
  userName?: string;
}

export interface AuthResult {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    refreshToken: string;
    email: string;
    userName?: string;
    name?: string;
    userId?: string;
    accessType?: string;
    /** Raw LoginResponseDto data — used by App.tsx to call syncBackendFromResponse */
    loginResponseData?: Record<string, any>;
  };
}

interface AuthFlowOptions {
  accessType?: string;
  skinType?: string;
}

// ─── Session helpers ──────────────────────────────────────────────────────────

/**
 * Check whether a valid session already exists in localStorage.
 */
export function checkStoredSession(): StoredSession | null {
  try {
    const token = window.localStorage.getItem(AUTH_KEYS.TOKEN);
    const email = window.localStorage.getItem(AUTH_KEYS.EMAIL);
    if (token && email) {
      const userName = window.localStorage.getItem(AUTH_KEYS.USER_NAME) ?? undefined;
      return { isAuthenticated: true, email, token, userName };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist an authenticated session to localStorage.
 *
 * Also writes the `userSession` key that `userStore.checkStoredAuth` requires
 * to restore the full Zustand session on next mount.  The shape mirrors what
 * `userStore.login()` stores so the existing restore logic works unchanged.
 */
export function persistSession(
  token: string,
  refreshToken: string,
  email: string,
  userName?: string,
  userInfo?: Record<string, any>,   // UserInfoDto from authenticate() response
  accessType: string = DEFAULT_ACCESS_TYPE,
): void {
  try {
    window.localStorage.setItem(AUTH_KEYS.TOKEN,     token);
    window.localStorage.setItem(AUTH_KEYS.REFRESH,   refreshToken);
    window.localStorage.setItem(AUTH_KEYS.EMAIL,     email);
    window.localStorage.setItem(AUTH_KEYS.TIMESTAMP, new Date().toISOString());
    window.localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TYPE, accessType);
    window.localStorage.setItem(AUTH_STORAGE_KEYS.NAV_RESET_ON_SIGNIN, '1');
    if (userName) {
      window.localStorage.setItem(AUTH_KEYS.USER_NAME, userName);
    }

    // Build and store the userSession so userStore.checkStoredAuth() can restore
    // the full session (AppUser + FamilyAccount) without re-authenticating.
    const ui = userInfo ?? {};
    const userId = ui.sraId || ui.accountId || email;

    const appUser = {
      userId,
      familyId:    ui.accountId ?? '',
      name:        ui.name || userName || email,
      email,
      role:        'primary',
      authToken:   token,
      refreshToken: refreshToken,
      lastLoginDate:  new Date().toISOString(),
      preferences:    {},
      appState:       { selectedProfileId: null },
      profilePermissions: {},
      createdDate:    ui.createdAt || new Date().toISOString(),
      lastActiveDate: new Date().toISOString(),
      deviceInfo:     { platform: 'web', appVersion: '1.0.0' },
    };

    const familyAccount = {
      familyId:           ui.accountId ?? '',
      familyName:         `${ui.name || userName || email}'s Family`,
      accountTier:        'standard',
      subscriptionStatus: ui.isActive === '1' ? 'active' : 'inactive',
      createdDate:        ui.createdAt || new Date().toISOString(),
      lastActiveDate:     new Date().toISOString(),
      settings: {
        dataRetentionDays: 365,
        allowDataSharing:  false,
        backupEnabled:     true,
        syncEnabled:       true,
      },
    };

    window.localStorage.setItem(
      AUTH_KEYS.SESSION,
      JSON.stringify({ user: appUser, familyAccount, isAuthenticated: true }),
    );
  } catch {
    // Ignore quota / security errors
  }
}

/**
 * Clear all auth data from localStorage (logout).
 *
 * Clears both this file's own key map (which includes web-only extras like
 * the stored password/signature/passkey ID) and the full shared
 * `ALL_AUTH_STORAGE_KEY_VALUES` set, so `auth_access_type` and
 * `progroups_navigation_reset_on_signin` are guaranteed cleared even if a
 * future edit reintroduces a raw string literal for one of them here.
 */
export function clearSession(): void {
  try {
    Object.values(AUTH_KEYS).forEach((key) => window.localStorage.removeItem(key));
    ALL_AUTH_STORAGE_KEY_VALUES.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Ignore
  }
}

// ─── Verification-code flow ───────────────────────────────────────────────────

/**
 * Step 1 of sign-in: ask the backend to send a verification code.
 *
 * Uses the account-service request type `signup_request_verify_existing`, which
 * requires the user's `name` (device / family-member label) as well as their email.
 * On success the backend returns an opaque `verificationToken` that must be
 * passed to `loginWithCode` in step 2.
 */
export async function sendVerificationCode(
  email:      string,
  userName:   string,
  apiService: ApiService,
  options: AuthFlowOptions = {},
): Promise<{ success: boolean; message: string; verificationToken?: string }> {
  const accessType = options.accessType || DEFAULT_ACCESS_TYPE;
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  try {
    const payload = JSON.stringify({
      name:       userName.trim(),
      email:      email.trim().toLowerCase(),
      accesstype: accessType,
      skintype:   skinType,
      clientid:   CLIENT_ID,
      signature:  ACCT_SIG,
    });

    const response = await (apiService as any).accountService({
      RequestType: 'signup_request_verify_existing',
      Payload:     payload,
    });

    if (response?.success) {
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(response.payload ?? '{}'); } catch { /* ignore */ }
      const verificationToken =
        (parsed.token as string | undefined) ||
        (response.result as string | undefined);
      if (verificationToken) {
        return { success: true, message: 'Verification code sent.', verificationToken };
      }
      return { success: false, message: 'No verification token received from server.' };
    }

    return {
      success: false,
      message: response?.message ?? 'Failed to send verification code.',
    };
  } catch (error: any) {
    return { success: false, message: error?.message ?? 'Failed to send verification code.' };
  }
}

/**
 * System sign-in verification - extended version with password and IP address.
 * 
 * Uses the same account-service request type `signup_request_verify_existing` but
 * includes additional fields (password, ipaddress) required for system access type (25).
 */
export async function sendSystemVerificationCode(
  email:      string,
  userName:   string,
  password:   string,
  apiService: ApiService,
  options: AuthFlowOptions = {},
): Promise<{ success: boolean; message: string; verificationToken?: string }> {
  const accessType = options.accessType || '25';
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  try {
    // Get client IP address from browser (note: this returns the public IP via external service)
    let ipAddress = '';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      ipAddress = ipData.ip || '';
    } catch {
      ipAddress = '';
    }

    const payload = JSON.stringify({
      name:       userName.trim(),
      email:      email.trim().toLowerCase(),
      password:   password,
      accesstype: accessType,
      skintype:   skinType,
      clientid:   CLIENT_ID,
      signature:  ACCT_SIG,
      bizname:    '',
      ipaddress:  ipAddress,
    });

    const response = await (apiService as any).accountService({
      RequestType: 'signup_request_verify_existing',
      Payload:     payload,
    });

    if (response?.success) {
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(response.payload ?? '{}'); } catch { /* ignore */ }
      const verificationToken =
        (parsed.token as string | undefined) ||
        (response.result as string | undefined);
      if (verificationToken) {
        return { success: true, message: 'Verification code sent.', verificationToken };
      }
      return { success: false, message: 'No verification token received from server.' };
    }

    return {
      success: false,
      message: response?.message ?? 'Failed to send verification code.',
    };
  } catch (error: any) {
    return { success: false, message: error?.message ?? 'Failed to send verification code.' };
  }
}

/**
 * Step 2 of sign-in: verify the code and obtain a JWT.
 *
 * Flow:
 *  2a. `signup_existing_account` – submit the OTP code + the verificationToken
 *      returned by step 1.  Backend returns temporary credentials.
 *  2b. `authenticate` – exchange those credentials for a proper JWT.
 */
export async function loginWithCode(
  email:             string,
  userName:          string,
  code:              string,
  verificationToken: string,
  apiService:        ApiService,
  options: AuthFlowOptions = {},
): Promise<AuthResult> {
  const accessType = options.accessType || DEFAULT_ACCESS_TYPE;
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  try {
    // 2a – verify code against the existing account
    const acctPayload = JSON.stringify({
      token:            verificationToken,
      verificationCode: code.trim(),
      signature:        ACCT_SIG,
    });

    const acctResponse = await (apiService as any).accountService({
      RequestType: 'signup_existing_account',
      Payload:     acctPayload,
    });

    if (!acctResponse?.success) {
      return {
        success: false,
        message: acctResponse?.message ?? 'Invalid or expired verification code.',
      };
    }

    let creds: Record<string, string> = {};
    try { creds = JSON.parse(acctResponse.payload ?? '{}'); } catch { /* ignore */ }

    if (!creds.email || !creds.password) {
      return { success: false, message: 'Account credentials not returned by server.' };
    }

    // 2b – authenticate with the returned credentials to get a JWT
    const authResponse = await (apiService as any).authenticate({
      Email:      creds.email,
      Name:       userName,
      Password:   creds.password,
      Signature:  creds.signature ?? ACCT_SIG,
      AccessType: accessType,
      SkinType:   skinType,
      ClientId:   CLIENT_ID,
    });

    if (authResponse?.success && authResponse.data?.token) {
      const { token, refreshToken, user: userInfo } = authResponse.data;
      const loginSettings = (authResponse.data as any).settings ?? (authResponse.data as any).Settings ?? [];
      persistSession(token, refreshToken ?? '', email, userName, userInfo ?? {}, accessType);
      try {
        window.localStorage.setItem(AUTH_KEYS.USER_SETTINGS, JSON.stringify(loginSettings));
      } catch { /* ignore quota/security errors */ }
      applySettingsToLocalStorage(loginSettings);
      // Store password + signature so DebugView "App Account Sign In" can re-authenticate
      try {
        if (creds.password)  window.localStorage.setItem(AUTH_KEYS.PASSWORD,  creds.password);
        if (creds.signature) window.localStorage.setItem(AUTH_KEYS.SIGNATURE, creds.signature ?? ACCT_SIG);
      } catch { /* ignore quota/security errors */ }
      apiService.setAuthToken(token);
      return {
        success: true,
        data: {
          token,
          refreshToken: refreshToken ?? '',
          email,
          userName,
          accessType,
          name:   (userInfo as any)?.name ?? creds.name ?? userName,
          userId: (userInfo as any)?.sraId ?? creds.sraId ?? creds.accountId,
          loginResponseData: authResponse.data,
        },
      };
    }

    return {
      success: false,
      message: authResponse?.message ?? 'Authentication failed. Please try again.',
    };
  } catch (error: any) {
    const status  = error?.response?.status ?? 0;
    const message = error?.response?.data?.message ?? error?.message ?? 'Sign-in failed';
    if (status === 401 || status === 400) {
      return { success: false, message: 'Incorrect code. Please check your email and try again.' };
    }
    return { success: false, message };
  }
}

// ─── New-account creation flow ────────────────────────────────────────────────

/**
 * Step 1 of sign-up: ask the backend to send a verification code for a NEW account.
 * Uses `signup_request_verify` (not the _existing variant used for sign-in).
 */
export async function sendNewAccountCode(
  email:      string,
  name:       string,
  bizname:    string,
  apiService: ApiService,
  options: AuthFlowOptions = {},
): Promise<{ success: boolean; message: string; verificationToken?: string }> {
  const accessType = options.accessType || DEFAULT_ACCESS_TYPE;
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  try {
    const payload = JSON.stringify({
      name:       name.trim(),
      bizname:    bizname.trim(),
      email:      email.trim().toLowerCase(),
      accesstype: accessType,
      skintype:   skinType,
      clientid:   CLIENT_ID,
      signature:  ACCT_SIG,
    });

    const response = await (apiService as any).accountService({
      RequestType: 'signup_request_verify',
      Payload:     payload,
    });

    if (response?.success) {
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(response.payload ?? '{}'); } catch { /* ignore */ }
      const verificationToken =
        (parsed.token as string | undefined) ||
        (response.result as string | undefined);
      if (verificationToken) {
        return { success: true, message: 'Verification code sent.', verificationToken };
      }
      return { success: false, message: 'No verification token received from server.' };
    }

    return {
      success: false,
      message: response?.message ?? 'Failed to send verification code.',
    };
  } catch (error: any) {
    return { success: false, message: error?.message ?? 'Failed to send verification code.' };
  }
}

/**
 * Step 2 of sign-up: create the account and authenticate.
 *
 *  2a. `signup_create_account` – submit OTP + verificationToken → backend returns credentials.
 *  2b. `authenticate`          – exchange credentials for a JWT.
 */
export async function createAccountWithCode(
  email:             string,
  name:              string,
  code:              string,
  verificationToken: string,
  apiService:        ApiService,
  options: AuthFlowOptions = {},
): Promise<AuthResult> {
  const accessType = options.accessType || DEFAULT_ACCESS_TYPE;
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  try {
    // 2a – create the account
    const acctResponse = await (apiService as any).accountService({
      RequestType: 'signup_create_account',
      Payload:     JSON.stringify({
        token:            verificationToken,
        verificationCode: code.trim(),
        signature:        ACCT_SIG,
      }),
    });

    if (!acctResponse?.success) {
      return {
        success: false,
        message: acctResponse?.message ?? 'Invalid or expired verification code.',
      };
    }

    let creds: Record<string, string> = {};
    try { creds = JSON.parse(acctResponse.payload ?? '{}'); } catch { /* ignore */ }

    if (!creds.email || !creds.password) {
      return { success: false, message: 'Account credentials not returned by server.' };
    }

    // 2b – authenticate to get a JWT
    const authResponse = await (apiService as any).authenticate({
      Email:      creds.email,
      Name:       name,
      Password:   creds.password,
      Signature:  creds.signature ?? ACCT_SIG,
      AccessType: accessType,
      SkinType:   skinType,
      ClientId:   CLIENT_ID,
    });

    if (authResponse?.success && authResponse.data?.token) {
      const { token, refreshToken, user: userInfo } = authResponse.data;
      const loginSettings = (authResponse.data as any).settings ?? (authResponse.data as any).Settings ?? [];
      persistSession(token, refreshToken ?? '', email, name, userInfo ?? {}, accessType);
      try {
        window.localStorage.setItem(AUTH_KEYS.USER_SETTINGS, JSON.stringify(loginSettings));
      } catch { /* ignore quota/security errors */ }
      applySettingsToLocalStorage(loginSettings);
      try {
        if (creds.password)  window.localStorage.setItem(AUTH_KEYS.PASSWORD,  creds.password);
        if (creds.signature) window.localStorage.setItem(AUTH_KEYS.SIGNATURE, creds.signature ?? ACCT_SIG);
      } catch { /* ignore quota/security errors */ }
      apiService.setAuthToken(token);
      return {
        success: true,
        data: {
          token,
          refreshToken: refreshToken ?? '',
          email,
          userName: name,
          accessType,
          name:   (userInfo as any)?.name ?? creds.name ?? name,
          userId: (userInfo as any)?.sraId ?? creds.sraId ?? creds.accountId,
          loginResponseData: authResponse.data,
        },
      };
    }

    return {
      success: false,
      message: authResponse?.message ?? 'Authentication failed after account creation.',
    };
  } catch (error: any) {
    const status  = error?.response?.status ?? 0;
    const message = error?.response?.data?.message ?? error?.message ?? 'Account creation failed';
    if (status === 401 || status === 400) {
      return { success: false, message: 'Incorrect code. Please check your email and try again.' };
    }
    return { success: false, message };
  }
}

// ─── WebAuthn / Passkey flow ──────────────────────────────────────────────────

/** True when the browser supports the WebAuthn Credential Management API. */
export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials?.get === 'function'
  );
}

/** True when a passkey credential ID is stored in localStorage for this device. */
export function hasStoredPasskey(): boolean {
  try {
    return !!window.localStorage.getItem(AUTH_KEYS.PASSKEY_ID);
  } catch {
    return false;
  }
}

/**
 * Full WebAuthn authentication flow:
 *
 *  1. POST /api/Authentication/passkey/challenge → backend returns challenge + options.
 *     - `allowCredentials` present  → user has an existing passkey → authenticate.
 *     - `allowCredentials` absent   → no passkey yet → register a new one.
 *  2. Call navigator.credentials.get() (auth) or navigator.credentials.create() (register).
 *  3. POST /api/Authentication/passkey/verify with challengeId + serialized credential.
 */
export async function authenticateWithPasskey(
  email:      string,
  userName:   string,
  apiService: ApiService,
  options: AuthFlowOptions = {},
): Promise<AuthResult> {
  const accessType = options.accessType || DEFAULT_ACCESS_TYPE;
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  if (!isPasskeySupported()) {
    return { success: false, message: 'Passkeys are not supported by this browser.' };
  }

  // Step 1 – request a challenge from the backend
  const challengeResult = await apiService.getPasskeyChallenge(email, accessType, "", userName, skinType);
  if (!challengeResult.success || !challengeResult.data) {
    return {
      success: false,
      message: challengeResult.message ?? 'Could not retrieve passkey challenge from server.',
    };
  }

  const cd = challengeResult.data;             // shorthand for challengeData
  const challenge = base64UrlToBuffer(cd.challenge);
  let credential: PublicKeyCredential | null;
  let isRegistration = false;

  // Step 2 – invoke the browser's authenticator
  try {
    if (cd.allowCredentials && cd.allowCredentials.length > 0) {
      // ── Authentication flow: user already has a registered passkey ──────────
      credential = (await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout:            cd.timeout,
          rpId:               cd.relyingPartyId,
          userVerification:   cd.userVerification ?? 'preferred',
          allowCredentials:   cd.allowCredentials.map((c: any) => ({
            id:         base64UrlToBuffer(c.id),
            type:       c.type,
            transports: c.transports,
          })),
        },
      })) as PublicKeyCredential | null;
    } else {
      // ── Registration flow: no passkey on record for this user ────────────────
      isRegistration = true;
      credential = (await navigator.credentials.create({
        publicKey: {
          challenge,
          timeout: cd.timeout,
          rp: {
            id:   cd.relyingPartyId,
            name: cd.relyingPartyName ?? 'Child Nav',
          },
          user: {
            id:          base64UrlToBuffer(cd.user.id),
            name:        cd.user.name,
            displayName: cd.user.displayName,
          },
          pubKeyCredParams: [
            { alg: -7,   type: 'public-key' }, // ES256
            { alg: -257, type: 'public-key' }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
          },
        },
      })) as PublicKeyCredential | null;
    }
  } catch (credError: any) {
    if (credError?.name === 'NotAllowedError') {
      return { success: false, message: 'Passkey authentication was cancelled.' };
    }
    return { success: false, message: credError?.message ?? 'Passkey authentication failed.' };
  }

  if (!credential) {
    return { success: false, message: 'No passkey credential returned by browser.' };
  }

  // Step 3 – verify credential on backend
  try {
    const assertion = serializeCredential(credential, isRegistration);
    const verifyResult = await apiService.verifyPasskeyAssertion(cd.challengeId, email, accessType, assertion);

    if (verifyResult.success && verifyResult.data?.token) {
      const { token, refreshToken, user } = verifyResult.data;
      const passkeySettings = (verifyResult.data as any).settings ?? (verifyResult.data as any).Settings ?? [];
      persistSession(token, refreshToken ?? '', email, userName, user ?? {}, accessType);
      try {
        window.localStorage.setItem(AUTH_KEYS.USER_SETTINGS, JSON.stringify(passkeySettings));
      } catch { /* ignore */ }
      applySettingsToLocalStorage(passkeySettings);
      apiService.setAuthToken(token);
      // Persist credential ID so hasStoredPasskey() knows a passkey exists on this device
      try {
        window.localStorage.setItem(AUTH_KEYS.PASSKEY_ID, credential.id);
      } catch { /* ignore */ }
      return {
        success: true,
        data: {
          token,
          refreshToken: refreshToken ?? '',
          email,
          userName,
          accessType,
          name:   (user as any)?.name,
          userId: (user as any)?.sraId ?? (user as any)?.accountId,
          loginResponseData: verifyResult.data,
        },
      };
    }

    return { success: false, message: verifyResult.message ?? 'Passkey verification failed.' };
  } catch (error: any) {
    return { success: false, message: error?.message ?? 'Passkey verification failed.' };
  }
}

/**
 * Register (save) a new passkey for the current user after a successful login.
 *
 * Flow:
 *  1. POST /api/Authentication/passkey/challenge with userId → backend returns
 *     registration options (challenge, rpId, user info — no allowCredentials).
 *  2. Call navigator.credentials.create() with those options.
 *  3. POST /api/Authentication/passkey/verify to complete registration.
 */
export async function registerPasskey(
  email:      string,
  userId:     string,
  userName:   string,
  apiService: ApiService,
  options: AuthFlowOptions = {},
): Promise<{ success: boolean; message?: string }> {
  const accessType = options.accessType || DEFAULT_ACCESS_TYPE;
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  if (!isPasskeySupported() || typeof navigator.credentials?.create !== 'function') {
    return { success: false, message: 'Passkey creation is not supported by this browser.' };
  }

  try {
    // Step 1 – obtain registration challenge from backend
    const challengeResult = await apiService.getPasskeyChallenge(email, accessType, userId, userName, skinType);
    if (!challengeResult.success || !challengeResult.data) {
      return {
        success: false,
        message: challengeResult.message ?? 'Could not retrieve passkey registration challenge.',
      };
    }
    const cd = challengeResult.data;

    // Step 2 – create credential using server-provided options
    const challenge = base64UrlToBuffer(cd.challenge);
    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge,
        timeout: cd.timeout ?? 60000,
        rp: {
          id:   cd.relyingPartyId ?? window.location.hostname,
          name: cd.relyingPartyName ?? 'Child Nav',
        },
        user: {
          id:          base64UrlToBuffer(cd.user?.id ?? btoa(userId)),
          name:        cd.user?.name ?? email,
          displayName: cd.user?.displayName ?? (userName || email),
        },
        pubKeyCredParams: [
          { alg: -7,   type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey:             'preferred',
          requireResidentKey:      false,
          userVerification:        cd.userVerification ?? 'preferred',
        },
      },
    })) as PublicKeyCredential | null;

    if (!credential) {
      return { success: false, message: 'No credential returned by browser.' };
    }

    // Step 3 – register with backend
    const assertion = serializeCredential(credential, true /* isRegistration */);
    const verifyResult = await apiService.verifyPasskeyAssertion(cd.challengeId, email, accessType, assertion);
    if (verifyResult.success) {
      try {
        window.localStorage.setItem(AUTH_KEYS.PASSKEY_ID, credential.id);
      } catch { /* ignore */ }
      return { success: true };
    }
    return { success: false, message: verifyResult.message ?? 'Failed to register passkey with server.' };
  } catch (error: any) {
    if (error?.name === 'NotAllowedError') {
      return { success: false, message: 'Passkey creation was cancelled.' };
    }
    return { success: false, message: error?.message ?? 'Failed to create passkey.' };
  }
}

export async function loginWithPasswordAndSecurityCode(
  email: string,
  password: string,
  securityCode: string,
  userName: string,
  apiService: ApiService,
  options: AuthFlowOptions = {},
): Promise<AuthResult> {
  const accessType = options.accessType || DEFAULT_ACCESS_TYPE;
  const skinType = options.skinType || DEFAULT_SKIN_TYPE;
  try {
    const authResponse = await (apiService as any).authenticate({
      Email: email.trim().toLowerCase(),
      Name: userName.trim(),
      Password: password,
      SecurityCode: securityCode.trim(),
      Signature: ACCT_SIG,
      AccessType: accessType,
      SkinType: skinType,
      ClientId: CLIENT_ID,
    });

    if (authResponse?.success && authResponse.data?.token) {
      const { token, refreshToken, user: userInfo } = authResponse.data;
      const loginSettings = (authResponse.data as any).settings ?? (authResponse.data as any).Settings ?? [];
      persistSession(token, refreshToken ?? '', email.trim().toLowerCase(), userName.trim(), userInfo ?? {}, accessType);
      try {
        window.localStorage.setItem(AUTH_KEYS.USER_SETTINGS, JSON.stringify(loginSettings));
      } catch { /* ignore quota/security errors */ }
      applySettingsToLocalStorage(loginSettings);
      try {
        window.localStorage.setItem(AUTH_KEYS.PASSWORD, password);
        window.localStorage.setItem(AUTH_KEYS.SIGNATURE, ACCT_SIG);
      } catch { /* ignore quota/security errors */ }
      apiService.setAuthToken(token);

      return {
        success: true,
        data: {
          token,
          refreshToken: refreshToken ?? '',
          email: email.trim().toLowerCase(),
          userName: userName.trim(),
          accessType,
          name: (userInfo as any)?.name ?? userName.trim(),
          userId: (userInfo as any)?.sraId ?? (userInfo as any)?.accountId,
          loginResponseData: authResponse.data,
        },
      };
    }

    return {
      success: false,
      message: authResponse?.message ?? 'Authentication failed. Please verify credentials and security code.',
    };
  } catch (error: any) {
    const status = error?.response?.status ?? 0;
    const message = error?.response?.data?.message ?? error?.message ?? 'Sign-in failed';
    if (status === 401 || status === 400) {
      return { success: false, message: 'Invalid credentials or security code.' };
    }
    return { success: false, message };
  }
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function base64UrlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
  const binary = window.atob(padded);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer.buffer;
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes  = new Uint8Array(buffer);
  let binary   = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/** Standard base64 WITH padding — required by C# Convert.FromBase64String() */
function bufferToBase64Standard(buffer: ArrayBuffer): string {
  const bytes  = new Uint8Array(buffer);
  let binary   = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return window.btoa(binary); // keep +, /, and = padding untouched
}

/**
 * Serialize a WebAuthn credential for submission to the backend verify endpoint.
 * Keys match the C# Pascal-case DTOs exactly:
 *   PasskeyAssertionDto : Id, RawId, Type, Response, ClientExtensionResults
 *   PasskeyResponseDto  : AuthenticatorData, ClientDataJSON, Signature,
 *                         UserHandle, PublicKey, PublicKeyAlgorithm
 */
function serializeCredential(credential: PublicKeyCredential, isRegistration: boolean): Record<string, unknown> {
  const response = credential.response as any;

  const serializedResponse: Record<string, unknown> = {
    // C# property is "ClientDataJSON" (all-caps JSON).
    // Must be standard base64 (with + / = padding) — C# Convert.FromBase64String rejects base64url.
    ClientDataJSON: bufferToBase64Standard(response.clientDataJSON),
    // AuthenticatorData — present on assertion; for attestation use getAuthenticatorData()
    AuthenticatorData: response.authenticatorData
      ? bufferToBase64Url(response.authenticatorData)
      : (typeof response.getAuthenticatorData === 'function'
          ? bufferToBase64Url(response.getAuthenticatorData())
          : undefined),
    Signature:  response.signature  ? bufferToBase64Url(response.signature)  : undefined,
    UserHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
  };

  if (isRegistration) {
    if (response.attestationObject) {
      serializedResponse.AttestationObject = bufferToBase64Url(response.attestationObject);
    }
    const publicKeyBuf: ArrayBuffer | null =
      typeof response.getPublicKey === 'function' ? response.getPublicKey() : response.publicKey ?? null;
    if (publicKeyBuf) {
      serializedResponse.PublicKey = bufferToBase64Url(publicKeyBuf);
      serializedResponse.PublicKeyAlgorithm =
        typeof response.getPublicKeyAlgorithm === 'function'
          ? response.getPublicKeyAlgorithm()
          : response.publicKeyAlgorithm ?? -7;
    }
  }

  return {
    Id:    credential.id,
    RawId: bufferToBase64Url(credential.rawId),
    Type:  credential.type,
    Response: serializedResponse,
  };
}
